import { EscolhaDoEmail } from "../componentes/console/ContatosDeAcesso";
import { useConsultaEmail } from "../nucleo/contatosDeAcesso";
import { useState } from "react";
import { Link } from "react-router";
import {
  Aviso,
  Botao,
  Campo,
  CampoDeTelefone,
  Cartao,
  GrupoDeOpcoes,
  Opcao,
} from "../componentes/ui";
import { RecursosPlusContratados } from "../componentes/console";
import { ErroDaApi } from "../nucleo/api";
import { useTitulo } from "../nucleo/titulo";
import { useVoltarParaHome } from "../nucleo/voltarParaHome";
import { cepCompleto } from "../nucleo/cep";
import { CampoDeCep } from "../componentes/cadastro/CampoDeCep";
import { SelecaoDeSegmento } from "../componentes/cadastro/SelecaoDeSegmento";
import { useEdicaoDoEndereco } from "../componentes/cadastro/useEdicaoDoEndereco";
import { cnpjValido, emailParecePlausivel, mascararCnpj, apenasDigitos, telefoneCompleto } from "../nucleo/validacao";
import {
  useCatalogo,
  erroDoContrato,
  useCriarImplantacao,
  type Catalogo,
  type NovaImplantacao as CorpoDaImplantacao,
  type RecursoDoCatalogo,
} from "../nucleo/implantacoes";

const PRAZO_PADRAO = 60;
const PRAZO_MAXIMO = 1095;

const ENDERECO_EM_BRANCO = {
  cep: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  uf: "",
};

interface Erros {
  [campo: string]: string | undefined;
}

function hojeEmIso(): string {
  // O input `type="date"` fala ISO. `toISOString` seria UTC e, à noite, adiantaria um dia para
  // quem está no Brasil — o suficiente para a trava de "assinatura no futuro" recusar hoje.
  const agora = new Date();
  const local = new Date(agora.getTime() - agora.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

/**
 * Tela 1 — o comercial abre a implantação.
 *
 * Preenche-se uma vez e o dado se propaga: o cliente não reinforma nada disso depois. Ao
 * salvar, o portal cria a organização (ou reaproveita a do mesmo CNPJ), abre a esteira e
 * **convida o contato** — os três num pedido só, porque uma implantação sem o acesso do
 * contato deixaria o comercial achando que avisou o cliente.
 *
 * O que esta tela **não** tem: campo de entrega prevista. A data é derivada da assinatura, da
 * liberação do ambiente e do prazo — digitá-la aqui seria abrir a porta que o modelo fechou.
 */
export function NovaImplantacao() {
  useTitulo("Nova implantação");
  const voltarParaHome = useVoltarParaHome();
  const catalogo = useCatalogo();
  const criar = useCriarImplantacao();

  const [razaoSocial, setRazaoSocial] = useState("");
  const [nomeFantasia, setNomeFantasia] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [endereco, setEndereco] = useState(ENDERECO_EM_BRANCO);
  const edicaoDoEndereco = useEdicaoDoEndereco();
  const [segmento, setSegmento] = useState("");

  const [assinadoEm, setAssinadoEm] = useState("");
  const [prazoDias, setPrazoDias] = useState(String(PRAZO_PADRAO));
  const [contrato, setContrato] = useState<File | null>(null);

  const [modulos, setModulos] = useState<string[]>([]);
  const [recursos, setRecursos] = useState<string[]>([]);

  const [ambiente, setAmbiente] = useState("cliente");
  const [erp, setErp] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [situacao, setSituacao] = useState("novo");

  const [contatoNome, setContatoNome] = useState("");
  const [contatoCargo, setContatoCargo] = useState("");
  const [contatoEmail, setContatoEmail] = useState("");
  const [contatoTelefone, setContatoTelefone] = useState("");
  const [vincularExistente, setVincularExistente] = useState(false);
  const consultaEmail = useConsultaEmail(contatoEmail, cnpj);
  const [podeConvidar, setPodeConvidar] = useState(true);
  // Quem já existe nesta empresa mantém o papel dele: a opção de convidar colegas só vale para quem
  // entra agora. É esse papel, e não a opção, que torna o WhatsApp do key user obrigatório.
  const entraComoKeyUser =
    (consultaEmail.data?.papel ?? (podeConvidar ? "admin_cliente" : "key_user")) === "key_user";

  const [consultorId, setConsultorId] = useState("");
  const [erros, setErros] = useState<Erros>({});

  /* Os módulos que a tela marcou sozinha, ao lado dos que o comercial marcou à mão. Existem só
     para dizer POR QUE eles apareceram marcados — um checkbox que muda sozinho e não se explica
     é indistinguível de um defeito. */
  const [modulosPeloRecurso, setModulosPeloRecurso] = useState<string[]>([]);

  function alternar(lista: string[], chave: string): string[] {
    return lista.includes(chave) ? lista.filter((item) => item !== chave) : [...lista, chave];
  }

  function trocarModulos(chave: string) {
    const novos = alternar(modulos, chave);
    setModulos(novos);
    setModulosPeloRecurso((atuais) => atuais.filter((marcado) => novos.includes(marcado)));
    // Desmarcar o módulo leva junto os Recursos Plus dele: deixá-los selecionados em silêncio
    // produziria um envio recusado pelo servidor por um motivo invisível na tela.
    const permitidos = new Set(
      (catalogo.data?.recursos_plus ?? [])
        .filter(
          (recurso) => recurso.modulo_chave === null || novos.includes(recurso.modulo_chave),
        )
        .map((recurso) => recurso.chave),
    );
    setRecursos((atuais) => atuais.filter((chaveDoRecurso) => permitidos.has(chaveDoRecurso)));
  }

  /* Um Recurso Plus pertence a um módulo, e o servidor recusa vender o Plus sem ele. Até
     03/09/2026 a tela cumpria essa regra ESCONDENDO o Plus enquanto o módulo não fosse marcado —
     e o efeito, apontado pela consultoria, era o catálogo de 25 aparecer com dois itens: quem
     abria a tela concluía que o resto não existia.

     Agora ela mostra os 25 e marca o módulo junto. É a leitura certa do ato: quem escolhe "NPS"
     está dizendo que vendeu CRM 360, não pedindo para vender um Plus órfão. O módulo marcado
     fica visível, o aviso diz de onde ele veio, e desmarcá-lo continua levando o Plus junto.

     `modulo_chave === null` é o recurso transversal — "Estoque por Data" serve ao Força de Vendas
     e ao B2B, o GuiaRep vale para a operação inteira. Não há módulo a marcar por ele. */
  function trocarRecurso(recurso: RecursoDoCatalogo) {
    const marcando = !recursos.includes(recurso.chave);
    setRecursos((atuais) => alternar(atuais, recurso.chave));
    if (!marcando) return;
    const moduloDoRecurso = recurso.modulo_chave;
    if (moduloDoRecurso === null || modulos.includes(moduloDoRecurso)) return;
    setModulos((atuais) => [...atuais, moduloDoRecurso]);
    setModulosPeloRecurso((atuais) => [...atuais, moduloDoRecurso]);
    // O módulo passou a existir: o erro "marque ao menos um módulo" não pode continuar na tela.
    setErros((atuais) => ({ ...atuais, modulos: undefined }));
  }

  function validar(): Erros {
    const encontrados: Erros = {};
    if (!razaoSocial.trim()) encontrados.razaoSocial = "Informe a razão social.";
    if (!cnpjValido(cnpj)) {
      encontrados.cnpj =
        "CNPJ inválido. Ele é a chave da empresa no portal — um dígito trocado cria um segundo cadastro do mesmo cliente.";
    }
    if (endereco.uf.trim() && endereco.uf.trim().length !== 2) {
      encontrados.uf = "Use a sigla de dois caracteres.";
    }
    if (endereco.cep.trim() && !cepCompleto(endereco.cep)) {
      encontrados.cep = "CEP inválido: informe os 8 dígitos.";
    }
    if (!assinadoEm) encontrados.assinadoEm = "Informe a data de assinatura do contrato.";
    else if (assinadoEm > hojeEmIso()) {
      encontrados.assinadoEm = "A assinatura não pode ser no futuro.";
    }
    const erroDoPdf = erroDoContrato(contrato);
    if (erroDoPdf) encontrados.contrato = erroDoPdf;

    const prazo = Number(prazoDias);
    if (!Number.isInteger(prazo) || prazo < 1 || prazo > PRAZO_MAXIMO) {
      encontrados.prazoDias = `Informe o prazo em dias, entre 1 e ${PRAZO_MAXIMO}.`;
    }
    if (modulos.length === 0) {
      encontrados.modulos = "Marque ao menos um módulo — são eles que definem o kickoff.";
    }
    if (!contatoNome.trim()) encontrados.contatoNome = "Informe quem vai receber o acesso.";
    if (!emailParecePlausivel(contatoEmail)) {
      encontrados.contatoEmail = "Informe um e-mail válido: é para ele que o convite vai.";
    }
    if (entraComoKeyUser && !telefoneCompleto(contatoTelefone)) {
      encontrados.contatoTelefone = "Informe o WhatsApp do key user com DDD e número.";
    }
    if (
      ["outra_empresa", "inativo"].includes(consultaEmail.data?.situacao ?? "")
    )
      encontrados.contatoEmail = consultaEmail.data?.mensagem;
    if (consultaEmail.data?.situacao === "existente" && !vincularExistente)
      encontrados.contatoEmail =
        "Vincule este usuário à implantação ou informe outro e-mail.";
    if (consultaEmail.consultando)
      encontrados.contatoEmail =
        "Aguarde a conferência do e-mail antes de salvar.";
    if (consultaEmail.isError)
      encontrados.contatoEmail = "Confira o e-mail novamente antes de salvar.";
    return encontrados;
  }

  function enderecoParaEnvio(): CorpoDaImplantacao["endereco"] {
    const texto = (valor: string) => valor.trim() || null;
    const campos = {
      cep: apenasDigitos(endereco.cep) || null,
      logradouro: texto(endereco.logradouro),
      numero: texto(endereco.numero),
      complemento: texto(endereco.complemento),
      bairro: texto(endereco.bairro),
      cidade: texto(endereco.cidade),
      uf: endereco.uf.trim().toUpperCase() || null,
    };
    return Object.values(campos).some((valor) => valor !== null) ? campos : null;
  }

  function montarCorpo(): CorpoDaImplantacao {
    return {
      empresa: {
        cnpj: apenasDigitos(cnpj),
        razao_social: razaoSocial.trim(),
        nome_fantasia: nomeFantasia.trim() || null,
        cidade: endereco.cidade.trim() || null,
        uf: endereco.uf.trim().toUpperCase() || null,
        segmento: segmento.trim() || null,
      },
      endereco: enderecoParaEnvio(),
      contrato_assinado_em: assinadoEm,
      prazo_dias: Number(prazoDias),
      modulos,
      recursos_plus: recursos,
      ambiente_tipo: ambiente,
      erp: erp.trim() || null,
      observacoes: observacoes.trim() || null,
      situacao_cliente: situacao,
      contato: {
        nome: contatoNome.trim(),
        cargo: contatoCargo.trim() || null,
        email: contatoEmail.trim().toLowerCase(),
        telefone: contatoTelefone.trim() || null,
        pode_convidar_colegas: podeConvidar,
        vincular_existente: vincularExistente,
      },
      consultor_id: consultorId,
    };
  }

  function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    const encontrados = validar();
    setErros(encontrados);
    if (Object.keys(encontrados).length > 0) return;

    // `mutate` e não `mutateAsync`: a recusa do servidor já vira `criar.error` e aparece na
    // tela. Com `mutateAsync` a mesma recusa viraria também uma promessa rejeitada sem dono,
    // que o navegador registra como erro não tratado — barulho para quem depura um 422 legítimo.
    criar.mutate(
      { corpo: montarCorpo(), contrato: contrato as File },
      {
        onSuccess: () => {
          // Cada lado volta para a sua home depois de salvar; a carteira com a recém-criada
          // continua a um clique ("Implantações" no menu).
          voltarParaHome("Implantação criada.");
        },
      },
    );
  }

  if (catalogo.isError) {
    return (
      <>
        <h1 className="titulo-pagina">Nova implantação</h1>
        <Aviso tom="erro" data-testid="falha-do-catalogo">
          Não foi possível carregar o catálogo de módulos. Sem ele não dá para registrar o que
          foi contratado — atualize a página em alguns instantes.
        </Aviso>
      </>
    );
  }

  const mensagemDeFalha =
    criar.error instanceof ErroDaApi
      ? criar.error.message
      : criar.error
        ? "Não foi possível abrir a implantação agora. Tente de novo em instantes."
        : null;

  return (
    <>
      <h1 className="titulo-pagina">Cadastrar cliente</h1>
      <p className="lead">
        Preencha uma vez. Estes dados se propagam para todas as etapas — o cliente não vai
        reinformar nada disso depois.
      </p>

      <form onSubmit={enviar} noValidate>
        <Cartao>
          <section className="formulario-secao">
            <h2 className="formulario-secao__titulo">Dados da empresa</h2>
            <p className="formulario-secao__dica">
              O CNPJ é a chave da empresa no portal. Se já existir um cadastro com ele, a
              implantação nova entra na mesma organização em vez de criar uma segunda.
            </p>
            <div className="grade-de-campos grade-de-campos--duas">
              <Campo
                rotulo="Razão social"
                obrigatorio
                value={razaoSocial}
                onChange={(evento) => setRazaoSocial(evento.target.value)}
                erro={erros.razaoSocial}
                autoComplete="organization"
              />
              <Campo
                rotulo="Nome fantasia"
                value={nomeFantasia}
                onChange={(evento) => setNomeFantasia(evento.target.value)}
                ajuda="É por ele que o cliente reconhece a própria empresa nas telas."
              />
            </div>
            <div className="grade-de-campos grade-de-campos--tres">
              <Campo
                rotulo="CNPJ"
                obrigatorio
                value={cnpj}
                onChange={(evento) => {
                  setCnpj(mascararCnpj(evento.target.value));
                  setVincularExistente(false);
                }}
                erro={erros.cnpj}
                inputMode="numeric"
                placeholder="00.000.000/0000-00"
              />
              <SelecaoDeSegmento value={segmento} onChange={setSegmento} placeholder="Confecção infantil" />
            </div>
          </section>

          <section className="formulario-secao" data-testid="secao-endereco">
            <h2 className="formulario-secao__titulo">Endereço</h2>
            <p className="formulario-secao__dica">
              Opcional. Digite o CEP e o portal preenche logradouro, bairro, cidade e UF; o número
              e o complemento são seus. Tudo continua editável.
            </p>
            <div className="grade-de-campos grade-de-campos--tres">
              <CampoDeCep
                value={endereco.cep}
                onChange={(cep) => setEndereco((atual) => ({ ...atual, cep }))}
                aoIniciarBusca={edicaoDoEndereco.iniciar}
                onEndereco={(achado) =>
                  setEndereco((atual) => edicaoDoEndereco.mesclar(atual, achado))
                }
                erro={erros.cep}
              />
              <Campo
                rotulo="Logradouro"
                value={endereco.logradouro}
                onChange={(evento) => {
                  edicaoDoEndereco.marcar("logradouro");
                  setEndereco((atual) => ({ ...atual, logradouro: evento.target.value }));
                }}
                autoComplete="address-line1"
              />
              <Campo
                rotulo="Número"
                value={endereco.numero}
                onChange={(evento) => setEndereco((atual) => ({ ...atual, numero: evento.target.value }))}
              />
            </div>
            <div className="grade-de-campos grade-de-campos--tres">
              <Campo
                rotulo="Complemento"
                value={endereco.complemento}
                onChange={(evento) => setEndereco((atual) => ({ ...atual, complemento: evento.target.value }))}
              />
              <Campo
                rotulo="Bairro"
                value={endereco.bairro}
                onChange={(evento) => {
                  edicaoDoEndereco.marcar("bairro");
                  setEndereco((atual) => ({ ...atual, bairro: evento.target.value }));
                }}
              />
              <Campo
                rotulo="Cidade"
                value={endereco.cidade}
                onChange={(evento) => {
                  edicaoDoEndereco.marcar("cidade");
                  setEndereco((atual) => ({ ...atual, cidade: evento.target.value }));
                }}
              />
            </div>
            <div className="grade-de-campos">
              <Campo
                rotulo="UF"
                value={endereco.uf}
                onChange={(evento) => {
                  edicaoDoEndereco.marcar("uf");
                  setEndereco((atual) => ({
                    ...atual,
                    uf: evento.target.value.toUpperCase().slice(0, 2),
                  }));
                }}
                erro={erros.uf}
                maxLength={2}
              />
            </div>
          </section>

          <section className="formulario-secao">
            <h2 className="formulario-secao__titulo">Contrato e prazo</h2>
            <p className="formulario-secao__dica">
              O prazo conta após a liberação do ambiente <b>e</b> a geração da tarefa de kickoff,
              a partir do último desses marcos. A entrega prevista é calculada e só aparece
              depois dos dois atos.
            </p>
            <div className="grade-de-campos grade-de-campos--duas">
              <Campo
                rotulo="Data de assinatura"
                obrigatorio
                type="date"
                max={hojeEmIso()}
                value={assinadoEm}
                onChange={(evento) => setAssinadoEm(evento.target.value)}
                erro={erros.assinadoEm}
              />
              <Campo
                rotulo="Prazo de entrega (dias)"
                obrigatorio
                type="number"
                min={1}
                max={PRAZO_MAXIMO}
                value={prazoDias}
                onChange={(evento) => setPrazoDias(evento.target.value)}
                erro={erros.prazoDias}
              />
            </div>
            <div className="grade-de-campos">
              <Campo
                rotulo="Contrato assinado (PDF)"
                obrigatorio
                type="file"
                accept="application/pdf,.pdf"
                onChange={(evento) => setContrato(evento.target.files?.[0] ?? null)}
                erro={erros.contrato}
                ajuda="Só PDF, até 10 MB. Sem o contrato anexado a implantação não é aberta."
              />
            </div>
          </section>

          <section className="formulario-secao">
            <h2 className="formulario-secao__titulo">Módulos contratados</h2>
            <p className="formulario-secao__dica">
              Definem quais trilhas de kickoff o cliente vai responder. As não contratadas nem
              aparecem para ele.
            </p>
            {catalogo.isPending ? (
              <p className="lead" role="status">
                Carregando o catálogo…
              </p>
            ) : (
              <GrupoDeOpcoes rotulo="Módulos contratados" rotuloOculto>
                {(catalogo.data as Catalogo).modulos.map((modulo) => (
                  <Opcao
                    key={modulo.chave}
                    tipo="checkbox"
                    name="modulo"
                    value={modulo.chave}
                    titulo={modulo.nome}
                    descricao={modulo.descricao ?? undefined}
                    checked={modulos.includes(modulo.chave)}
                    onChange={() => trocarModulos(modulo.chave)}
                  />
                ))}
              </GrupoDeOpcoes>
            )}
            {erros.modulos ? (
              <p className="campo__erro" role="alert">
                {erros.modulos}
              </p>
            ) : null}
          </section>

          <section className="formulario-secao">
            <h2 className="formulario-secao__titulo">Recursos Plus contratados</h2>
            <p className="formulario-secao__dica">
              Registrar aqui é o que faz o cliente ver o material certo e a implantação não
              esquecer o que foi vendido.
            </p>
            {catalogo.isPending ? (
              <p className="lead" role="status">
                Carregando o catálogo…
              </p>
            ) : (
              <RecursosPlusContratados
                recursos={(catalogo.data as Catalogo).recursos_plus}
                modulos={(catalogo.data as Catalogo).modulos}
                selecionados={recursos}
                modulosContratados={modulos}
                aoAlternar={trocarRecurso}
              />
            )}
            {modulosPeloRecurso.length > 0 ? (
              <Aviso tom="marca" className="mt-4" data-testid="modulos-marcados-pelo-recurso">
                Marcamos{" "}
                <b>
                  {modulosPeloRecurso
                    .map(
                      (chave) =>
                        catalogo.data?.modulos.find((modulo) => modulo.chave === chave)?.nome ??
                        chave,
                    )
                    .join(", ")}
                </b>{" "}
                em <i>Módulos contratados</i>: o Recurso Plus que você escolheu pertence a ele. Se
                o contrato não inclui o módulo, desmarque lá em cima — o Recurso Plus sai junto.
              </Aviso>
            ) : null}
          </section>

          <section className="formulario-secao">
            <h2 className="formulario-secao__titulo">Ambiente e ERP</h2>
            <p className="formulario-secao__dica">
              O que o comercial já sabe da negociação. Não é o formulário da infra — são as
              informações que definem o caminho da implantação e o que a infraestrutura precisa
              provisionar.
            </p>
            <GrupoDeOpcoes rotulo="Onde o ambiente GEOvendas vai rodar?">
              {(catalogo.data?.ambientes ?? []).map((opcao) => (
                <Opcao
                  key={opcao.valor}
                  tipo="radio"
                  name="ambiente"
                  value={opcao.valor}
                  titulo={opcao.rotulo}
                  checked={ambiente === opcao.valor}
                  onChange={() => setAmbiente(opcao.valor)}
                />
              ))}
            </GrupoDeOpcoes>
            <div className="grade-de-campos grade-de-campos--duas mt-4">
              <Campo
                rotulo="ERP do cliente"
                value={erp}
                onChange={(evento) => setErp(evento.target.value)}
                placeholder="Linx, Sisplan, TOTVS…"
                list="erps-conhecidos"
              />
              <div className="campo">
                <label className="campo__rotulo" htmlFor="observacoes-do-cadastro">
                  Observações
                </label>
                <textarea
                  id="observacoes-do-cadastro"
                  className="campo__controle"
                  rows={3}
                  maxLength={4000}
                  value={observacoes}
                  onChange={(evento) => setObservacoes(evento.target.value)}
                  placeholder="O que a implantação precisa saber (vai para a descrição do épico no Jira)"
                />
              </div>
              <datalist id="erps-conhecidos">
                <option value="Linx" />
                <option value="Sisplan" />
                <option value="TOTVS" />
                <option value="Senior" />
              </datalist>
              <div className="campo">
                <label className="campo__rotulo" htmlFor="situacao-do-cliente">
                  Situação do cliente
                </label>
                <select
                  id="situacao-do-cliente"
                  className="campo__controle"
                  value={situacao}
                  onChange={(evento) => setSituacao(evento.target.value)}
                >
                  {(catalogo.data?.situacoes ?? []).map((opcao) => (
                    <option key={opcao.valor} value={opcao.valor}>
                      {opcao.rotulo}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <section className="formulario-secao">
            <h2 className="formulario-secao__titulo">Pessoa de contato</h2>
            <p className="formulario-secao__dica">
              Quem vai receber o acesso ao portal, preencher as informações e acompanhar a
              implantação. O convite sai por e-mail assim que você salvar.
            </p>
            <div className="grade-de-campos grade-de-campos--duas">
              <Campo
                rotulo="Nome"
                obrigatorio
                value={contatoNome}
                onChange={(evento) => setContatoNome(evento.target.value)}
                erro={erros.contatoNome}
              />
              <Campo
                rotulo="Cargo"
                value={contatoCargo}
                onChange={(evento) => setContatoCargo(evento.target.value)}
              />
            </div>
            <div className="grade-de-campos grade-de-campos--duas">
              <Campo
                rotulo="E-mail"
                obrigatorio
                type="email"
                value={contatoEmail}
                onChange={(evento) => {
                  setContatoEmail(evento.target.value);
                  setVincularExistente(false);
                }}
                erro={erros.contatoEmail}
                autoComplete="email"
              />
              <EscolhaDoEmail
                email={contatoEmail}
                cnpj={cnpj}
                vincular={vincularExistente}
                aoVincular={setVincularExistente}
                aoCorrigir={() => {
                  setContatoEmail("");
                  setVincularExistente(false);
                }}
              />
              <CampoDeTelefone
                rotulo={entraComoKeyUser ? "WhatsApp do key user" : "Telefone / WhatsApp"}
                obrigatorio={entraComoKeyUser}
                valor={contatoTelefone}
                aoMudar={setContatoTelefone}
                erro={erros.contatoTelefone}
              />
            </div>
            <GrupoDeOpcoes rotulo="Permissões do contato" rotuloOculto>
              <Opcao
                tipo="checkbox"
                name="pode-convidar"
                titulo="Este contato pode convidar colegas para responderem partes do kickoff"
                descricao="Marcado, ele entra como administrador da conta; desmarcado, como key user."
                checked={podeConvidar}
                onChange={() => setPodeConvidar((atual) => !atual)}
              />
            </GrupoDeOpcoes>
          </section>

          {/* Consultor e responsável pela venda são a mesma pessoa e um campo só (requisito 3 da
              Amanda, 01/10/2026): quem fechou o negócio é o consultor da conta, e é o contato do
              cliente até o módulo dele chegar em Treinamento. O `data-testid` antigo foi mantido
              de propósito, para as especificações de navegador não mudarem de assunto. */}
          <section className="formulario-secao">
            <h2 className="formulario-secao__titulo">Consultor da conta</h2>
            <p className="formulario-secao__dica">
              Quem fechou o negócio e acompanha a conta. É o contato que o cliente vê até o
              módulo dele chegar em Treinamento.
            </p>
            <div className="campo">
              <label className="campo__rotulo" htmlFor="consultor-da-conta">
                Consultor da conta
              </label>
              <select
                id="consultor-da-conta"
                className="campo__controle"
                value={consultorId}
                required
                data-testid="responsavel-pela-venda"
                onChange={(evento) => setConsultorId(evento.target.value)}
              >
                <option value="">Selecione</option>
                {(catalogo.data?.consultores ?? []).map((pessoa) => (
                  <option key={pessoa.id} value={pessoa.id}>
                    {pessoa.nome}
                  </option>
                ))}
              </select>
            </div>
          </section>

          {mensagemDeFalha ? (
            <Aviso tom="erro" className="mt-5" data-testid="falha-do-cadastro">
              {mensagemDeFalha}
            </Aviso>
          ) : null}

          <div className="barra-de-acoes">
            <p className="barra-de-acoes__aviso">
              Ao salvar: o portal cria a esteira do cliente, libera a etapa de infraestrutura e
              envia o convite de acesso para o contato.
            </p>
            <Link to="/admin/implantacoes" className="botao botao--fantasma">
              Cancelar
            </Link>
            {/* O consultor da conta é obrigatório: o `select` já traz `required`, e o botão
                acompanha para que a recusa não dependa de qual navegador está do outro lado. */}
            <Botao
              type="submit"
              disabled={
                criar.isPending ||
                !consultorId ||
                consultaEmail.consultando ||
                consultaEmail.isError ||
                ["outra_empresa", "inativo"].includes(
                  consultaEmail.data?.situacao ?? "",
                ) ||
                (consultaEmail.data?.situacao === "existente" &&
                  !vincularExistente)
              }
            >
              {criar.isPending
                ? "Criando…"
                : "Criar implantação e enviar acesso"}
            </Botao>
          </div>
        </Cartao>
      </form>
    </>
  );
}
